import { parseOptions } from './Utils.js'
import Deferred from './Deferred.js'

/**
 * @typedef PromiseOptions
 * @type {object}
 * @property {number} limit - The number of requests to run concurrently
 * @property {boolean} handleReject - Whether to throw an error or return one
 */

/**
 * Borrowed liberally from https://github.com/caolan/async/blob/812184055566b7803b89d8ef8dc6a93ea3b95eda/lib/auto.js
 * @param {Object<string|any>} tasks
 * @param {PromiseOptions} options
 * @returns {Promise<Object<string,any>>}
 */
async function pipeline( tasks, options ) {
  const opts = parseOptions( options, Infinity )
  const numTasks = Object.keys( tasks ).length
  if ( !numTasks ) {
    return null
  }
  const deferred = Deferred.deferred()

  const limit = Math.min( numTasks, opts.limit )

  const results = {}
  const readyTasks = []
  const readyToCheck = []
  const uncheckedDeps = {}
  // eslint-disable-next-line prefer-const
  let runningTasks = 0, canceled = false, hasError = false, listeners = {}

  Object
    .keys( tasks )
    .forEach( ( key ) => {
      const taskItem = tasks[key]
      const { task, dependencies : deps } = getDependenciesAndTaskForTaskItem( taskItem )

      if ( deps.length === 0 ) {
        enqueueTask( key, [ task ] )
        readyToCheck.push( key )
        return
      }

      let remainingDependencies = deps.length

      uncheckedDeps[key] = remainingDependencies
      
      deps.forEach( ( dep ) => {
        if ( !tasks[dep] ) {
          throw new Error( `pipeline task ${key} has non-existent dependency ${dep} in ${deps.join( ', ' )}` )
        }

        addListener( dep, () => {
          remainingDependencies--
          if ( remainingDependencies === 0 ) {
            enqueueTask( key, [ ...deps, task ] )
          }
        } )
      } )
    } )

  checkForDeadlocks()
  processQueue()

  function enqueueTask( key, task ) {
    readyTasks.push( () => runTask( key, task ) )
  }

  function processQueue() {
    if ( canceled ) return
    if ( readyTasks.length === 0 && runningTasks === 0 ) {
      deferred.resolve( results )
      return
    }
    while ( readyTasks.length && runningTasks < limit ) {
      const run = readyTasks.shift()
      run()
    }

  }

  function addListener( taskName, fn ) {
    let taskListeners = listeners[taskName]
    if ( !taskListeners ) {
      listeners[taskName] = []
      taskListeners = listeners[taskName]
    }

    taskListeners.push( fn )
  }

  function taskComplete( taskName ) {
    const taskListeners = listeners[taskName] || []
    taskListeners.forEach( fn => fn() )
    processQueue()
  }

  async function runTask( key, task ) {
    if ( hasError ) return

    runningTasks++
    const taskFn = task[task.length - 1]
    let args = []

    if ( task.length > 1 ) {
      args = task.slice( 0, task.length - 1 )
      args = args.map( arg => results[arg] )
    }

    try {
      let result
      // If the "task" is a function, run it and await it if it's async
      if ( typeof taskFn === 'function' ) {
        result = taskFn( ...args )
        if ( result?.then ) {
          result = await result
        }
        // If the task isn't a function, it's value is the result
      } else {
        result = taskFn
      }

      runningTasks--

      results[key] = result
      taskComplete( key )
    } catch ( err ) {
      if ( opts.handleReject ) {
        const safeResults = {}
        Object.keys( results ).forEach( ( rkey ) => {
          safeResults[rkey] = results[rkey]
        } )
        safeResults[key] = err
        hasError = true
        listeners = {}

        deferred.resolve( safeResults )
        return
      }

      deferred.reject( err )
    }
  }

  function checkForDeadlocks() {
    // Kahn's algorithm
    // https://en.wikipedia.org/wiki/Topological_sorting#Kahn.27s_algorithm
    // http://connalle.blogspot.com/2013/10/topological-sortingkahn-algorithm.html
    let currentTask
    let counter = 0
    while ( readyToCheck.length ) {
      currentTask = readyToCheck.pop()
      counter++
      getDependents( currentTask ).forEach( ( dependent ) => {
        if ( --uncheckedDeps[dependent] === 0 ) {
          readyToCheck.push( dependent )
        }
      } )
    }

    if ( counter !== numTasks ) {
      throw new Error(
        'pipeline cannot execute tasks due to a recursive dependency'
      )
    }
  }

  function getDependents( taskName ) {
    const result = []
    Object.keys( tasks ).forEach( ( key ) => {
      const task = tasks[key]
      const { dependencies : deps } = getDependenciesAndTaskForTaskItem( task )
      if ( Array.isArray( deps ) && deps.indexOf( taskName ) >= 0 ) {
        result.push( key )
      }
    } )
    return result
  }

  return deferred
}

function getDependenciesAndTaskForTaskItem( taskItem ) {
  let dependencies, task
  if ( Array.isArray( taskItem ) ) {
    dependencies = taskItem.slice( 0, taskItem.length - 1 )
    task = taskItem[taskItem.length - 1]
  } else if ( taskItem?.execute ) {
    dependencies = taskItem.requires || []
    task = taskItem.execute
  } else {
    task = taskItem
    dependencies = []
  }

  return { dependencies, task }
}

export default {
  pipeline
}
